import { Loading } from '@wfp/react';
import get from 'lodash/get';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';

import { requestAPI } from 'src/redux/data/actions';
import { GET_APP_CONFIGURATION } from 'src/redux/view/constants';
import { buildErrorComponents, showNotification } from 'src/utils';
import NoAccess from '../NoAccess';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';
import Header from './Header';

export class BaseLayoutComponent extends Component {
  appStatuses = {
    pending: 'pending',
    needsInit: 'needsInit',
    ready: 'ready',
  };

  constructor(props) {
    super(props);

    this.initApp = this.initApp.bind(this);
    this.configureApp = this.configureApp.bind(this);
    this.state = {
      appStatus: this.appStatuses.pending,
      data: null,
    };
  }

  componentDidMount() {
    const { API: API = {}, ...rest } = window.djconfig;
    window.djconfig = {
      APIS: {
        GET_APP_CONFIGURATION: `/acr/frontend-api/app-conf/`,
        ...API,
      },
      ...rest,
    };

    if (this.state.appStatus === this.appStatuses.pending) {
      if (!this.isAppConfLoaded()) {
        this.setState({ appStatus: this.appStatuses.needsInit });
      } else {
        this.setState({ appStatus: this.appStatuses.ready });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.appStatus === this.appStatuses.needsInit &&
      prevState.appStatus !== this.state.appStatus
    ) {
      this.props.fetchAppConf();
    }
    if (!!prevProps.isFetching && !this.props.isFetching) {
      if (this.isAppConfLoaded()) {
        const { data = {} } = this.props;
        this.setState({ data }, this.configureApp);
      } else {
        this.setState({ appStatus: this.appStatuses.needsInit });
      }
    }

    if (prevProps.error !== this.props.error && this.props.error !== null) {
      const errorsList = buildErrorComponents(this.props.error);

      showNotification(<>{errorsList.map(error => error)}</>, 'error', {
        autoClose: false,
        closeOnClick: false,
      });
    }
  }

  initApp = () => {
    this.setState({ needsInitialization: false }, this.props.fetchAppConf);
  };

  configureApp = () => {
    const { APIS: APIS_djconfig = {}, ...rest_djconfig } = window.djconfig;
    const { APIS: APIS_loaded = {}, ...rest_loaded } = this.props.data;

    window.djconfig = {
      APIS: {
        ...APIS_djconfig,
        ...APIS_loaded,
      },
      ...rest_djconfig,
      ...rest_loaded,
    };
    this.setState({ appStatus: this.appStatuses.ready });
  };

  isAppConfLoaded = () => {
    return !this.props.error && !this.props.isFetching && !!this.props.data;
  };

  render() {
    let content;
    let userInfo = {};
    let hasAnyPermission = false;
    let { data } = this.props;
    if (data === null) {
      data = this.state.data;
    }
    if (data) {
      userInfo = get(data, 'userInfo', {});
      if (userInfo) {
        hasAnyPermission = get(userInfo, 'hasAnyPermission', false);
      }
    }

    if (this.props.error !== null) {
      content = <></>;
    } else if (this.state.appStatus !== this.appStatuses.ready) {
      content = <Loading withOverlay={true} className="page-loader" />;
    } else if (!hasAnyPermission) {
      content = <NoAccess />;
    } else {
      const routesComponents = [];
      const makeRoutes = (routes, keyPrefix = '') => {
        for (const [routeKey, subRoute] of Object.entries(routes)) {
          const recursiveKeyPrefix = `${keyPrefix}-${routeKey}`;

          if (subRoute.hasOwnProperty('path')) {
            routesComponents.push(
              <Route
                key={recursiveKeyPrefix}
                path={subRoute.path}
                exact={true}
                element={<subRoute.component />}
              />,
            );
          }

          if (subRoute.hasOwnProperty('sub_routes')) {
            makeRoutes(subRoute.sub_routes, recursiveKeyPrefix);
          }
        }
      };
      makeRoutes(this.props.routesConfig.root.sub_routes);

      const isNotFoundPage =
        this.props.location &&
        this.props.location.pathname.includes('/not-found');

      const isAdminDashboardWithoutPermission =
        this.props.location &&
        this.props.location.pathname.includes('/admin-dashboard') &&
        data &&
        !get(data, 'userInfo.hasDashboardPermission', false);

      const isUsersManagementWithoutPermission =
        this.props.location &&
        this.props.location.pathname.includes('/users-management') &&
        data &&
        !get(data, 'userInfo.hasUserManagementPermission', false);

      const shouldHideBreadcrumb =
        isNotFoundPage ||
        isAdminDashboardWithoutPermission ||
        isUsersManagementWithoutPermission;

      content = (
        <>
          {!shouldHideBreadcrumb && <Breadcrumb />}
          <Routes>{routesComponents}</Routes>
        </>
      );
    }

    return (
      <div className="page-content">
        <div className="main-footer-padding">
          <Header routesConfig={this.props.routesConfig} />
          {content}
        </div>
        <Footer />
      </div>
    );
  }
}

export const mapStateToProps = state => {
  return {
    routesConfig: state.routesConfig,
    data: null,
    isFetching: false,
    error: null,
    ...state.viewData[GET_APP_CONFIGURATION],
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    fetchAppConf: () => {
      dispatch(requestAPI(GET_APP_CONFIGURATION));
    },
  };
};

const BaseLayout = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaseLayoutComponent);

const BaseLayoutWithLocation = props => {
  const location = useLocation();
  return <BaseLayout {...props} location={location} />;
};

export { BaseLayoutWithLocation };
